import React from 'react';
import styled from 'styled-components';
import { RatingBadge, Box, Flex, Heading, Text } from 'pcln-design-system';
import { themeGet } from '@styled-system/theme-get';
import PropTypes from 'prop-types';
import Icon from 'components/Icon';
import { regexURL } from '../../constants';

const ReviewContainer = styled.div`
    display: flex;
    border: 1px solid;
    border-color: ${themeGet('colors.borderGray')};
    background-color: ${themeGet('colors.white')};
    padding: ${themeGet('space.4')}px;
    flex-direction: column;
    border-radius: ${themeGet('radius')};
    word-break: break-word;

    ${themeGet('mediaQueries.md')} {
        flex-direction: row;
    }
`;
const ReviewRatingBadge = styled(RatingBadge)`
    word-break: keep-all;
`;
const ReviewText = styled(Text)`
    font-size: ${themeGet('fontSizes.1')}px;
    line-height: 20px;
    padding-left: ${themeGet('space.3')}px;
    padding-bottom: ${themeGet('space.3')}px;
`;
const ReviewTextGeneral = styled(ReviewText)`
    padding-left: 0px;
`;
const ReviewSignature = styled(Text)`
    font-size: ${themeGet('fontSizes.0')}px;
    line-height: 20px;
    font-weight: 600;
    color: ${themeGet('colors.gray')};
`;
const ReviewHeading = styled(Heading)`
    font-size: ${themeGet('fontSizes.3')}px;
    font-weight: 600;
    line-height: 25px;
    display: inline;
`;
const ReviewLeft = styled.div`
    display: flex;
    align-items: flex-start;
    margin: 0 ${themeGet('space.4')}px ${themeGet('space.3')}px 0;
    ${themeGet('mediaQueries.md')} {
        margin-bottom: 0;
        flex: 0 0 230px;
    }
`;
const ReviewFlex = styled.div`
    display: flex;
    align-items: flex-start;
`;

const redactLinks = text => {
    return typeof text === 'string' ? text.replace(regexURL, '[Link Redacted]') : '';
};

const Review = ({ review, ...props }) => {
    const generalText = redactLinks(review.getTextGeneral());
    const negativeText = redactLinks(review.getTextNegative());
    const positiveText = redactLinks(review.getTextPositive());

    return (
        <ReviewContainer itemProp="review" itemScope itemType="http://schema.org/Review" {...props}>
            <ReviewLeft>
                <ReviewRatingBadge itemProp="reviewRating" itemScope itemType="http://schema.org/Rating">
                    <meta itemProp="worstRating" content="0" />
                    <meta itemProp="bestRating" content="10" />
                    <span itemProp="ratingValue">{review.getScore()}</span>
                </ReviewRatingBadge>
                <Box ml={10}>
                    <ReviewHeading itemProp="author">{review.getGuestName()}</ReviewHeading>
                    {review.getTravelerTypeName() && (
                        <ReviewSignature>Traveling as a {review.getTravelerTypeName()}</ReviewSignature>
                    )}
                    <ReviewSignature itemProp="datePublished">{review.getDate()}</ReviewSignature>
                </Box>
            </ReviewLeft>
            <div>
                <div itemProp="reviewBody">
                    {generalText && <ReviewTextGeneral>{generalText}</ReviewTextGeneral>}
                    {positiveText && (
                        <ReviewFlex>
                            <Icon name="thumbsUp" size={18} color="gray" viewBox="3 3 18 18" />
                            <ReviewText>{positiveText}</ReviewText>
                        </ReviewFlex>
                    )}
                    {negativeText && (
                        <ReviewFlex>
                            <Icon name="thumbsDown" size={18} color="gray" viewBox="3 3 18 18" />
                            <ReviewText>{negativeText}</ReviewText>
                        </ReviewFlex>
                    )}
                    {!generalText && !positiveText && !negativeText && (
                        <ReviewTextGeneral>This guest did not leave comments about this hotel.</ReviewTextGeneral>
                    )}
                </div>
                <Flex flex-wrap="wrap">
                    <ReviewSignature>
                        Verified review from a<Icon name={review.getSource()} width={52} mx={8} mb={-8} color="blue" />
                        customer
                    </ReviewSignature>
                </Flex>
            </div>
        </ReviewContainer>
    );
};
Review.propTypes = {
    review: PropTypes.object.isRequired,
    className: PropTypes.string,
    key: PropTypes.string
};

/**
 * @component
 */
export default Review;
export { default as ReviewSnippet } from './ReviewSnippet';
